// libs
import React, { useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

// constants
import { ARIA_LABELS } from 'shared/layouts/Coupons/constants';
import { ROLES, TAB_INDEX } from 'shared/constants';
import { TEXTS } from 'shared/layouts/Coupons/constants';
import { LOGO_TYPE } from 'shared/constants';

// components
import { OverlaySheetContext, SHEET_TRANSITION } from 'shared/components/OverlaySheet';
import ValidityText from 'shared/layouts/Coupons/CouponsExpiry/CouponValdity/CouponValidity';
import CouponDeltaCommunication from 'shared/layouts/Coupons/components/DeltaCommunication/DeltaCommunication';

// context
import { useLoaderContext } from 'shared/components/LoaderProvider/context';

// svgs
import CircleCrossWhite from '@nykaa/ui-components/Icons/circle-x-duotone';
import ArrowLeft from '@nykaa/ui-components/Icons/arrow-left';

// styles
import {
  ViewDetailsContainer,
  CloseIcon,
  Header,
  OverlaySheet,
  CouponInfo,
  OfferTitle,
  Subtitle,
  ActionContainer,
  ActionButtonOffer,
  BottomContainer,
  InfoContainer,
  DiscountTagWrapper,
  SaveUptoText,
} from './styles';

// Aria Label Constants
const LOCAL_ARIA_LABELS = {
  GO_BACK: 'Go back',
};

// types
import { OfferDetails } from 'shared/layouts/Coupons/types';
import Logo from 'shared/components/Logo';

// defs
interface ViewDetailsNudgeProps {
  onHide: () => void;
  offerDetails: OfferDetails;
  className?: string;
  sheetTransition?: SHEET_TRANSITION;
  actionContainerText?: string;
  showBackButton?: boolean;
  showOverlay?: boolean;
  isEligibleForDeltaCommunication?: boolean;
}

function ViewDetailsNudge({
  onHide,
  offerDetails,
  className = '',
  sheetTransition = SHEET_TRANSITION.BOTTOM,
  actionContainerText = TEXTS.OKAY,
  showBackButton = false,
  showOverlay = true,
  isEligibleForDeltaCommunication,
}: ViewDetailsNudgeProps) {
  const { showLoader, hideLoader } = useLoaderContext();
  const closeButtonRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap and auto-focus
  useEffect(() => {
    // Small delay to ensure DOM is ready after Portal renders
    const focusTimer = setTimeout(() => {
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
    }, 100);

    // Store original tabindex values for background elements
    const backgroundElements: Array<{ element: HTMLElement; originalTabIndex: string | null; }> = [];

    // Disable background focusable elements
    const disableBackgroundFocus = () => {
      const allFocusable = document.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      allFocusable.forEach((el) => {
        // Only disable if NOT inside our modal
        if (modalRef.current && !modalRef.current.contains(el)) {
          const originalTabIndex = el.getAttribute('tabindex');
          backgroundElements.push({ element: el, originalTabIndex });
          el.setAttribute('tabindex', '-1');
          el.setAttribute('data-focus-disabled', 'true');
        }
      });
    };

    // Restore background elements
    const restoreBackgroundFocus = () => {
      backgroundElements.forEach(({ element, originalTabIndex }) => {
        if (originalTabIndex !== null) {
          element.setAttribute('tabindex', originalTabIndex);
        } else {
          element.removeAttribute('tabindex');
        }
        element.removeAttribute('data-focus-disabled');
      });
    };

    // Focus trap
    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      const focusableArray = Array.from(focusableElements);
      if (focusableArray.length === 0) return;

      const firstElement = focusableArray[0];
      const lastElement = focusableArray[focusableArray.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (event.shiftKey) {
        // Shift + Tab
        if (activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    disableBackgroundFocus();
    document.addEventListener('keydown', handleTabKey);

    return () => {
      clearTimeout(focusTimer);
      restoreBackgroundFocus();
      document.removeEventListener('keydown', handleTabKey);
    };
  }, []);

  if (isEmpty(offerDetails)) {
    showLoader({});
  } else hideLoader();

  const {
    title,
    description,
    label = '',
    offerDiscount = '',
    expiryEpoch = 0,
    deltaTextModified = '',
    status,
    offerId,
  } = offerDetails;

  const handleKeyDown = (e: React.KeyboardEvent, callback: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  return (
    <OverlaySheet
      onHide={onHide}
      disableNav
      transitionFrom={sheetTransition}
      hideOnOverlayClick
      className={className}
      showOverlay={showOverlay}
    >
      <OverlaySheetContext.Consumer>
        {({ doHide }) => (
          <ViewDetailsContainer
            ref={modalRef}
            className="view-details-container"
            data-modal-active="true"
          >
            <Header showBottomBorder>
              {showBackButton ? (
                <SaveUptoText>
                  <CloseIcon
                    ref={closeButtonRef}
                    onClick={doHide}
                    onKeyDown={(e) => handleKeyDown(e, doHide)}
                    aria-label={LOCAL_ARIA_LABELS.GO_BACK}
                    role={ROLES.BUTTON}
                    tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                  >
                    <ArrowLeft aria-hidden="true" />{' '}
                  </CloseIcon>
                  {label ? label : `${TEXTS.SAVE_OFFER} ${offerDiscount}`}
                </SaveUptoText>
              ) : (
                <CloseIcon
                  ref={closeButtonRef}
                  onClick={doHide}
                  onKeyDown={(e) => handleKeyDown(e, doHide)}
                  aria-label={ARIA_LABELS.CLOSE_OFFER_DETAILS}
                  role={ROLES.BUTTON}
                  tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                >
                  <CircleCrossWhite aria-hidden="true" />
                </CloseIcon>
              )}
            </Header>
            <InfoContainer className="offer-info">
              <CouponInfo>
                <DiscountTagWrapper>
                  <Logo type={LOGO_TYPE.OFFERS} />
                </DiscountTagWrapper>
                <OfferTitle className="title">{title}</OfferTitle>
                <Subtitle className="subtitle">{description}</Subtitle>
                {expiryEpoch > 0 && <ValidityText expiryEpoch={expiryEpoch} />}
                {isEligibleForDeltaCommunication && (
                  <CouponDeltaCommunication
                    deltaModifiedText={deltaTextModified}
                    isDetailsView
                    isOffer={true}
                    status={status}
                    offerId={offerId}
                    couponData={offerDetails}
                    isPDP
                  />
                )}
              </CouponInfo>
            </InfoContainer>

            <BottomContainer showTopBorder={false}>
              <ActionContainer>
                <ActionButtonOffer
                  kind={'secondary'}
                  size={'large'}
                  shape={'default'}
                  onClick={doHide}
                >
                  {actionContainerText}
                </ActionButtonOffer>
              </ActionContainer>
            </BottomContainer>
          </ViewDetailsContainer>
        )}
      </OverlaySheetContext.Consumer>
    </OverlaySheet>
  );
}

export default ViewDetailsNudge;
